/**
 * Página de criação de novos recursos.
 *
 * Este componente renderiza um formulário para criar novos recursos,
 * permitindo selecionar categoria, tipo, nome e adicionar uma descrição
 * usando um editor de texto rico.
 *
 * @module NewResources
 */

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import Alert from '@mui/material/Alert';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Button from '../../components/Button/Button';
import ContainerPage from '../../components/ContainerPage/ContainerPage';
import TextEditor from '../../components/Resources/TextEditor/TextEditor';
import { useNewResource } from '../../hooks/useNewResource';
import api from '../../services/api';
import { AlertContainer, Back, FormStyle, Main } from './NewResources.Styles';

/**
 * Componente funcional que renderiza o formulário de criação de recursos.
 *
 * Utiliza o hook personalizado useNewResource para gerenciar o estado
 * e as operações relacionadas à criação de recursos.
 *
 * @returns {JSX.Element} Componente de criação de recursos
 */
const NewResources = () => {
  // Hook de tradução para internacionalização
  const { t } = useTranslation();

  // Estado local para armazenar as categorias disponíveis
  const [categories, setCategories] = useState([]);

  // Estados e funções do hook personalizado useNewResource
  const {
    category,
    setCategory,
    type,
    setType,
    name,
    setName,
    editorContent,
    setEditorContent,
    createResource,
    errorMsg,
    successMsg,
    errors,
  } = useNewResource();

  /**
   * Efeito que carrega as categorias quando o componente é montado.
   */
  useEffect(() => {
    fetchCategories();
  }, []);

  /**
   * Busca todas as categorias disponíveis da API.
   *
   * @async
   * @function fetchCategories
   */
  const fetchCategories = async () => {
    try {
      const { data } = await api.get('categories');
      setCategories(data.resources);
    } catch (err) {
      console.error('Erro na requisição', err);
    }
  };

  return (
    <ContainerPage>
      {/* Cabeçalho com botão de voltar */}
      <Back>
        <button>
          <Link to={'/Resources'}>
            <ArrowBackIosNewIcon htmlColor="black" fontSize="medium" />
          </Link>
        </button>
        <span>{t('New Resource')}</span>
      </Back>

      {/* Conteúdo principal */}
      <Main>
        <FormStyle>
          <form onSubmit={createResource}>
            {/* Seletor de categoria */}
            <select
              name="category"
              className={errors.category ? 'error' : ''}
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="" disabled hidden>
                {t('Category')}
              </option>
              {categories?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>

            {/* Campo para tipo de recurso */}
            <input
              type="text"
              id="type"
              name="type"
              className={errors.type ? 'error' : ''}
              placeholder={t('Enter resource type')}
              value={type}
              onChange={(e) => setType(e.target.value)}
            />

            {/* Campo para nome do recurso */}
            <input
              type="text"
              name="name-resource"
              id="name"
              className={errors.name ? 'error' : ''}
              placeholder={t('Resource Name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            {/* Editor de texto para descrição do recurso */}
            <div className="insert-text">
              <TextEditor
                className={errors.editor ? 'error' : ''}
                value={editorContent}
                onChange={setEditorContent}
              />
            </div>

            {/* Botão de salvar */}
            <div className="button-save">
              <Button text={t('Save')} variant={'lg'} />
            </div>
          </form>
        </FormStyle>
      </Main>

      {/* Alertas de erro ou sucesso */}
      {(errorMsg || successMsg) && (
        <AlertContainer>
          {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
          {successMsg && <Alert severity="success">{successMsg}</Alert>}
        </AlertContainer>
      )}
    </ContainerPage>
  );
};

export default NewResources;
